import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Flex, Button, Box, Text, Modal, Message } from '@workwhile/ui'
import { ChevronLeft } from 'lucide-react'
import { Header } from 'components/Header'
import * as ROUTES from 'constants/routes'
import { useDayforceWebhookDetailContextValue } from '../context/DayforceWebhookDetailProvider'
import { DayforceWebhookEventTypes } from 'pages/Dayforce/constants'
import { JobInvitation } from './JobInvitation'
import { UnknownWebhook } from './UnknownWebhook'
import { GeneralDetails } from './GeneralDetails'
import { JobUpdated } from './JobUpdated'
import { JobCanceled } from './JobCanceled'
import { WorkerClockedInOut } from './WorkerClockedInOut'
import { WorkerNcns } from './WorkerNcns'
import { ShiftUpdated } from './ShiftUpdated'

const canArchiveWebhook = (status: string) => {
  return status !== 'completed'
}

const ArchiveWebhookButton = () => {
  const {
    state: { archiveMutationState },
    actions: { archive },
  } = useDayforceWebhookDetailContextValue()

  const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false)

  useEffect(() => {
    if (archiveMutationState.isSuccess) {
      setIsArchiveConfirmOpen(false)
    }
  }, [archiveMutationState.isSuccess])

  return (
    <>
      <Button variant="secondary" onClick={() => setIsArchiveConfirmOpen(true)}>
        Archive webhook
      </Button>
      <Modal
        open={isArchiveConfirmOpen}
        onClose={() => {
          if (!archiveMutationState.isPending) {
            setIsArchiveConfirmOpen(false)
          }
        }}
        title="Archive this webhook?"
      >
        <Text>The webhook will be marked as completed and archived.</Text>
        {archiveMutationState.isError ? (
          <Message variant="error">{archiveMutationState.errorMessage}</Message>
        ) : null}
        <Flex mt={4} gap={2}>
          <Button
            variant="secondary"
            onClick={() => setIsArchiveConfirmOpen(false)}
            disabled={archiveMutationState.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => archive()}
            loading={archiveMutationState.isPending}
            disabled={archiveMutationState.isPending}
          >
            Archive webhook
          </Button>
        </Flex>
      </Modal>
    </>
  )
}

export const DayforceWebhookDetailMain = () => {
  const {
    state: { data },
  } = useDayforceWebhookDetailContextValue()

  const descriptionBlock = useMemo(() => {
    if (data.eventType === 'job_invitation') {
      return <JobInvitation />
    }
    if (data.eventType === 'job_updated') {
      return <JobUpdated />
    }
    if (data.eventType === 'job_canceled') {
      return <JobCanceled />
    }
    if (data.eventType === 'worker_clocked_in') {
      return <WorkerClockedInOut />
    }
    if (data.eventType === 'worker_clocked_out') {
      return <WorkerClockedInOut />
    }
    if (data.eventType === 'worker_clock_out_allowed') {
      return <WorkerClockedInOut />
    }
    if (data.eventType === 'worker_ncns') {
      return <WorkerNcns />
    }
    if (data.eventType === 'shift_updated') {
      return <ShiftUpdated />
    }
    return <UnknownWebhook />
  }, [data.eventType])

  return (
    <>
      <Header
        subTitle={
          data?.eventType
            ? DayforceWebhookEventTypes[data.eventType] ?? data.eventType
            : undefined
        }
        title={`Webhook #${data.id} - ${DayforceWebhookEventTypes[data.eventType] ?? data.eventType}`}
        leftContent={
          <Button
            as={Link}
            to={ROUTES.DAYFORCE_WEBHOOKS}
            variant="secondary"
            kind="small"
            style={{ textDecoration: 'none' }}
          >
            <Flex alignItems="center" gap={1}>
              <ChevronLeft size={16} />
              Back
            </Flex>
          </Button>
        }
      />
      <Box margin={3}>
        <Flex flexDirection="column" gap={3}>
          <GeneralDetails
            actionsBlock={
              <>
                {canArchiveWebhook(data.status) ? (
                  <ArchiveWebhookButton />
                ) : (
                  <Text>Webhook archived</Text>
                )}
              </>
            }
          />
          {descriptionBlock}
        </Flex>
      </Box>
    </>
  )
}
